import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Icon from './Icon';
import Note from './Note';

// A panel that slides up over the screen.
//
// Used for the four groups on the home screen, which are the things a customer
// opens a few times ever. Home stays underneath and is always one dismiss away,
// so there is no navigation to get lost in and no back button to mis-press.
//
// Not used for anything launched from a corner: a panel should appear where it
// was asked for, and a sheet from the account icon would arrive at the far end
// of the screen from the tap. That is what Menu is for.
// What a setting does after it is saved.
//
// Nothing on the phone moves when you flip a switch here -- the value is saved
// to the server, and the television collects it on its own thirty-second poll.
// Without saying so, the honest sequence reads as a broken app: the customer
// flips the theme, watches their living room not change, and flips it back.
//
// It lives in the sheet rather than in each panel so that a sheet added later
// cannot forget it. The one place the wait is different is the box tab, where
// the thing being sent is a command rather than a setting, so that sheet passes
// its own.
// Exported because the home screen needs the same promise for the recitation
// button, which sends its change the same way and waits the same amount of
// time. One string, so the two can never quote different numbers.
export const SETTLE_NOTE = 'Changes reach the television within about 30 seconds. '
  + 'There is no need to reload or restart it.';

const Sheet = ({ title, subtitle, onClose, onBack, children, footnote = SETTLE_NOTE }) => {
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);

    // Move focus into the sheet so a keyboard or screen reader follows it
    // rather than being left behind on the tile that opened it.
    const first = ref.current?.querySelector('button, select, input, a');
    first?.focus({ preventScroll: true });

    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <button className="scrim" onClick={onClose} aria-label="Close" tabIndex={-1} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="sheet__grab" />
        <div className="sheet__head">
          {onBack && (
            <button className="sheet__back" onClick={onBack} aria-label="Back">
              <Icon name="back" size={20} />
            </button>
          )}
          <h3>{title}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>
        {subtitle && <p className="sheet__sub">{subtitle}</p>}
        {children}
        {footnote && <Note className="sheet__note">{footnote}</Note>}
      </div>
    </>
  );
};

Sheet.propTypes = {
  title:    PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  onClose:  PropTypes.func.isRequired,
  onBack:   PropTypes.func,
  children: PropTypes.node,
  // Pass null on a sheet where nothing is being sent anywhere.
  footnote: PropTypes.string,
};

export default Sheet;
